// frontend/src/admin/v2/JobDetail.jsx
import { useState } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import {
  Stack, Box, Typography, Chip, Button, Skeleton, Alert,
  IconButton, Collapse, useTheme, useMediaQuery,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import RefreshButton from '../../components/common/RefreshButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useJob } from './hooks/useJobs'
import { SP, FS, hoverOnly } from '../../theme/tokens'
import Panel from '../../components/common/Panel'
import { formatBerlinDate } from '../../utils/time'
import LsmInventorySyncPanel from './components/LsmInventorySyncPanel'
import { formatDuration } from '../../utils/format'

/**
 * JobDetail — /admin/jobs/<key>.
 *
 * @description
 *   Full view of a single scheduled job. Header with status pill + Run-now
 *   + Refresh. Stats strip. Run-history timeline showing last 50 runs with
 *   inline error snippets, expandable per row to show full last_error.
 *
 *   When a run is `running`, the page polls every 15s so the user sees
 *   progress without manual refresh.
 */
const STATUS_TONE = {
  green: { color: 'success.main', label: 'Healthy' },
  amber: { color: 'warning.main', label: 'Watch' },
  red:   { color: 'error.main',   label: 'Failed' },
}

const RUN_STATUS_COLOR = {
  done:      'success.main',
  error:     'error.main',
  cancelled: 'text.disabled',
  cancel_requested: 'text.disabled',
  running:   'info.main',
}

function relTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  const diffMin = Math.round((Date.now() - d.getTime()) / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  return formatBerlinDate(d)
}

// Dieselbe dreistufige Dauer wie `JobsList` und `JobCard` — jetzt EINE Quelle
// (`format.js`), statt dreimal wortgleich kopiert.
function fmtDuration(s) {
  return formatDuration(s)
}


function RunRow({ run }) {
  const [expanded, setExpanded] = useState(false)
  const dotColor = RUN_STATUS_COLOR[run.status] || 'action.hover'
  return (
    <>
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={SP.tight}
        sx={{
          py: 0.5,
          cursor: run.error_snippet ? 'pointer' : 'default',
          ...hoverOnly(run.error_snippet ? { bgcolor: 'action.hover' } : {}),
          px: SP.tight,
          borderRadius: 1,
        }}
        onClick={() => run.error_snippet && setExpanded(e => !e)}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, mt: 0.75, flexShrink: 0 }} />
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
          <Stack direction="row" spacing={SP.tight} alignItems="baseline" flexWrap="wrap">
            <Typography sx={{ fontSize: FS.sm, fontWeight: 500, fontFamily: 'monospace' }}>
              {run.status}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
              {relTime(run.started_at)}
            </Typography>
            {run.duration_s != null && (
              <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled' }}>
                · {fmtDuration(run.duration_s)}
              </Typography>
            )}
          </Stack>
          {run.error_snippet && !expanded && (
            <Typography
              sx={{
                fontSize: FS.xs, color: 'error.main',
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
              }}
            >
              {run.error_snippet}
            </Typography>
          )}
        </Stack>
        {run.error_snippet && (
          <IconButton size="small" sx={{ p: 0.25 }}
            aria-label={expanded ? 'Collapse error details' : 'Expand error details'}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ ml: 3, mb: 1, p: SP.card, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography
            sx={{
              fontSize: FS.xs, fontFamily: 'monospace',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              color: 'text.primary',
            }}
          >
            {run.error_snippet}
          </Typography>
        </Box>
      </Collapse>
    </>
  )
}


export default function JobDetail() {
  const { key } = useParams()
  const { data, loading, error, refetch, runNow } = useJob(key)
  const [running, setRunning] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleRun = async () => {
    if (running) return
    setRunning(true)
    try {
      await runNow()
    } finally {
      setTimeout(() => setRunning(false), 1500)
    }
  }

  const tone = data?.status ? STATUS_TONE[data.status] : null
  const stats = data?.stats_24h || {}

  return (
    <Stack spacing={SP.section}>
      {/* Back link */}
      <Stack direction="row" alignItems="center" spacing={SP.tight}>
        <IconButton component={RouterLink} to="/admin/jobs" size="small" aria-label="Back to all jobs">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography
          component={RouterLink}
          to="/admin/jobs"
          variant="caption"
          sx={{
            fontSize: FS.xs, color: 'text.secondary',
            textDecoration: 'none', ...hoverOnly({ textDecoration: 'underline' }),
          }}
        >
          All jobs
        </Typography>
      </Stack>

      {error && <Alert severity="error" variant="outlined">{error}</Alert>}

      {!data && loading && (
        <Stack spacing={SP.section}>
          <Skeleton variant="text" width="40%" height={48} />
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={400} />
        </Stack>
      )}

      {data && (
        <>
          {/* Header */}
          <Stack
            direction={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'flex-start' : 'flex-start'}
            justifyContent="space-between"
            spacing={SP.card}
          >
            <Stack spacing={SP.tight} sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={SP.tight}>
                <Typography variant="h5" sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {data.label}
                </Typography>
                {tone && (
                  <Chip
                    label={tone.label}
                    size="small"
                    sx={{
                      fontSize: FS.xs, fontWeight: 600,
                      color: tone.color, bgcolor: 'transparent',
                      border: 1, borderColor: tone.color,
                    }}
                  />
                )}
              </Stack>
              <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary', fontFamily: 'monospace' }}>
                {data.kind} · {data.schedule_desc}
              </Typography>
              {data.description && (
                <Typography variant="body2" sx={{ fontSize: FS.sm, color: 'text.secondary', maxWidth: 720 }}>
                  {data.description}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={SP.tight}>
              {/* 🔴 E8 (2026-10-07): hier stand ein `IconButton` in einem
                  `Tooltip` OHNE `aria-label` — der Knopf hatte also gar keinen
                  zugaenglichen Namen (Grill W9). */}
              <RefreshButton ariaLabel="Refresh job detail" onClick={refetch} />
              <Button
                variant="contained"
                size="small"
                startIcon={<PlayArrowIcon className={running ? 'spin-180' : ''} sx={{ fontSize: 16 }} />}
                onClick={handleRun}
                disabled={running}
              >
                {running ? 'Triggered' : 'Run now'}
              </Button>
            </Stack>
          </Stack>

          {/* Stats card */}
          <Panel pad="card">
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={SP.section}
              divider={!isMobile ? <Box sx={{ borderRight: 1, borderColor: 'divider' }} /> : null}
            >
              <Stack spacing={0.25}>
                <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Last run
                </Typography>
                <Typography sx={{ fontSize: FS.md, fontWeight: 500 }}>
                  {data.last_run
                    ? `${data.last_run.status} · ${relTime(data.last_run.started_at)}`
                    : 'never'}
                </Typography>
                {data.last_run?.duration_s != null && (
                  <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled' }}>
                    {fmtDuration(data.last_run.duration_s)}
                  </Typography>
                )}
              </Stack>
              <Stack spacing={0.25}>
                <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  24h success
                </Typography>
                <Typography sx={{ fontSize: FS.md, fontWeight: 500 }}>
                  {stats.ok || 0}/{stats.total || 0}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled' }}>
                  {stats.failed || 0} failed
                </Typography>
              </Stack>
              <Stack spacing={0.25}>
                <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Avg duration
                </Typography>
                <Typography sx={{ fontSize: FS.md, fontWeight: 500 }}>
                  {fmtDuration(stats.avg_duration_s)}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled' }}>
                  successful runs only
                </Typography>
              </Stack>
              {data.runbook && (
                <Stack spacing={0.25}>
                  <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                    Runbook
                  </Typography>
                  <Typography
                    component="a"
                    href={`https://github.com/BrewoutCoffeeBot/purchase-order-generator/blob/master/docs/knowledge/${data.runbook}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    sx={{
                      fontSize: FS.sm, color: 'primary.main',
                      textDecoration: 'none',
                      display: 'inline-flex', alignItems: 'center', gap: 0.5,
                      ...hoverOnly({ textDecoration: 'underline' }),
                    }}
                  >
                    Open docs <OpenInNewIcon sx={{ fontSize: 12 }} />
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Panel>

          {key === 'lsm-inventory-sync' && <LsmInventorySyncPanel onRan={refetch} />}

          {/* Run history */}
          <Panel pad="card">
            <Stack spacing={SP.tight}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" sx={{ fontSize: FS.sm, fontWeight: 600 }}>
                  Recent runs · last 50
                </Typography>
                <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled' }}>
                  Click a failed row to see full error
                </Typography>
              </Stack>

              {!data.history || data.history.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: SP.card, textAlign: 'center' }}>
                  No run history yet — trigger one with the Run now button.
                </Typography>
              ) : (
                <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
                  {data.history.map(run => <RunRow key={run.id} run={run} />)}
                </Stack>
              )}
            </Stack>
          </Panel>
        </>
      )}

      <style>{`
        /* W-68: 600 -> 250 ms. Ein 180-Grad-Dreh ist ein Umschalt-Indikator;
           600 ms liessen ihn traege wirken. Budget fuer UI-Uebergaenge: < 300 ms. */
        .spin-180 { animation: spin180 0.25s ease-out; }
        @keyframes spin180 { to { transform: rotate(180deg); } }
      `}</style>
    </Stack>
  )
}
